/**
 * Custom CKEditor 5 plugin.
 *
 * Registers two toolbar buttons:
 *   - 'tableNoBorders'    : acts on the currently selected table.
 *   - 'tableNoBordersAll' : acts on every table in the document.
 *
 * For each affected table it:
 *   - removes all borders on the table and every one of its cells, and
 *   - sets the table width to 100%.
 *
 * It relies on the attributes registered by the TableProperties and
 * TableCellProperties plugins, so those must be present in the build.
 */
import Plugin from '@ckeditor/ckeditor5-core/src/plugin.js';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview.js';

// A clear, high-contrast 20x20 icon: a solid blue table grid with a bold red
// "no" circle-slash overlaid on top, so the "remove borders" action is obvious.
const tableNoBordersIcon =
    '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">' +
    // Filled table so the shape reads clearly at small sizes.
    '<rect x="1.5" y="3" width="17" height="14" rx="1" fill="#1565c0"/>' +
    '<g stroke="#ffffff" stroke-width="1.4">' +
    '<path d="M1.5 8h17M1.5 12.5h17M7 3v14M12.5 3v14"/>' +
    '</g>' +
    // Bold red "prohibited" badge to signal removal.
    '<circle cx="14.5" cy="14.5" r="5" fill="#ffffff"/>' +
    '<circle cx="14.5" cy="14.5" r="4.25" fill="none" stroke="#d32f2f" stroke-width="1.6"/>' +
    '<path d="M11.7 17.3 17.3 11.7" stroke="#d32f2f" stroke-width="1.6" stroke-linecap="round"/>' +
    '</svg>';

export default class TableNoBorders extends Plugin {
    static get pluginName() {
        return 'TableNoBorders';
    }

    init() {
        const editor = this.editor;
        const t = editor.t;

        // By default CKEditor downcasts `tableWidth` onto the wrapping
        // <figure class="table"> only, so the inner <table> keeps its intrinsic
        // width. Mirror the width onto the actual <table> element too.
        this._setupTableWidthDowncast();

        // Button 1: selected table only.
        editor.ui.componentFactory.add( 'tableNoBorders', locale => {
            const view = new ButtonView( locale );

            view.set( {
                label: t( 'No borders / full width (this table)' ),
                icon: tableNoBordersIcon,
                tooltip: true
            } );

            view.bind( 'isEnabled' ).to( editor, 'isReadOnly', isReadOnly => {
                return !isReadOnly && !!this._getSelectedTable();
            } );

            this.listenTo( view, 'execute', () => {
                const table = this._getSelectedTable();

                if ( table ) {
                    editor.model.change( writer => this._stripTable( writer, table ) );
                }

                editor.editing.view.focus();
            } );

            return view;
        } );

        // Button 2: every table in the document.
        editor.ui.componentFactory.add( 'tableNoBordersAll', locale => {
            const view = new ButtonView( locale );

            view.set( {
                label: t( 'No borders / full width (all tables)' ),
                icon: tableNoBordersIcon,
                withText: false,
                tooltip: true
            } );

            view.bind( 'isEnabled' ).to( editor, 'isReadOnly', isReadOnly => !isReadOnly );

            this.listenTo( view, 'execute', () => {
                this._applyNoBordersToAll();
                editor.editing.view.focus();
            } );

            return view;
        } );
    }

    /**
     * Adds a downcast converter that also applies the model `tableWidth`
     * attribute as an inline `width` style on the inner <table> element (both in
     * the editing view and in the saved data), so the table itself — not just
     * the wrapping <figure> — gets the width.
     */
    _setupTableWidthDowncast() {
        const editor = this.editor;

        const converter = dispatcher => {
            dispatcher.on( 'attribute:tableWidth:table', ( evt, data, conversionApi ) => {
                const viewWriter = conversionApi.writer;
                const mapped = conversionApi.mapper.toViewElement( data.item );

                if ( !mapped ) {
                    return;
                }

                // Depending on the pipeline, the mapped element may be the
                // wrapping <figure class="table"> or the <table> itself.
                let table = null;

                if ( mapped.is( 'element', 'table' ) ) {
                    table = mapped;
                } else {
                    table = Array.from( mapped.getChildren() )
                        .find( child => child.is( 'element', 'table' ) );
                }

                if ( !table ) {
                    return;
                }

                if ( data.attributeNewValue ) {
                    viewWriter.setStyle( 'width', data.attributeNewValue, table );
                } else {
                    viewWriter.removeStyle( 'width', table );
                }
            }, { priority: 'low' } );
        };

        editor.conversion.for( 'editingDowncast' ).add( converter );
        editor.conversion.for( 'dataDowncast' ).add( converter );
    }

    /**
     * Returns the table element that contains the current selection, or null.
     */
    _getSelectedTable() {
        const selection = this.editor.model.document.selection;
        const positionParent = selection.getFirstPosition().parent;

        return positionParent.findAncestor
            ? positionParent.findAncestor( 'table' )
            : this._findAncestorTable( positionParent );
    }

    _findAncestorTable( element ) {
        let node = element;

        while ( node ) {
            if ( node.is && node.is( 'element', 'table' ) ) {
                return node;
            }

            node = node.parent;
        }

        return null;
    }

    /**
     * Removes borders on the given table and all of its cells, and sets the
     * table width to 100%. Must be called inside a model.change() block.
     */
    _stripTable( writer, table ) {
        // Table-level: no border + full width.
        writer.setAttribute( 'tableBorderStyle', 'none', table );
        writer.setAttribute( 'tableBorderWidth', '0', table );
        writer.removeAttribute( 'tableBorderColor', table );
        writer.setAttribute( 'tableWidth', '100%', table );

        // Cell-level: remove borders on every cell in the table.
        for ( const row of table.getChildren() ) {
            if ( !row.is( 'element', 'tableRow' ) ) {
                continue;
            }

            for ( const cell of row.getChildren() ) {
                writer.setAttribute( 'tableCellBorderStyle', 'none', cell );
                writer.setAttribute( 'tableCellBorderWidth', '0', cell );
                writer.removeAttribute( 'tableCellBorderColor', cell );
            }
        }
    }

    /**
     * Applies the no-borders / full-width transformation to every table in the
     * document, as a single undo step.
     */
    _applyNoBordersToAll() {
        const model = this.editor.model;
        const root = model.document.getRoot();
        const tables = [];

        for ( const element of root.getChildren() ) {
            if ( element.is( 'element', 'table' ) ) {
                tables.push( element );
            }
        }

        if ( !tables.length ) {
            return;
        }

        model.change( writer => {
            for ( const table of tables ) {
                this._stripTable( writer, table );
            }
        } );
    }
}
